/**
 *
 * Organizations/Show/CampaignTemplates/Form
 *
 */

import React, { useEffect, useState } from 'react'
import FloatingLabel from '@joeyparis/react-floating-label-input'
import PropTypes from 'prop-types'

// GraphQL
import { useLazyQuery, useMutation, gql } from '@apollo/client'

// Components
import DragAndDropListSingleInput from 'components/DragAndDropList'
import Description2 from 'components/Campaigns/Form/Description2'
import ImageUploader from 'components/ImageUploader'
import MarkdownEditor from 'components/MarkdownEditor'
import MutationStatusIndicator from 'components/MutationStatusIndicator'
import Modal from 'components/Modal'
import LandingPageBackgroundSelector from 'components/LandingPageBackgroundSelector'

import { Campaign } from 'globalVars'
import { useCampaignTopicOptions } from 'hooks'

const return_fields = `
	id
	name
	topic
	headline
	highlights
	description
	description2
	disclaimer_json
	questions
	landing_page_background_id
	objective
	is_meal_event
	video_embed_link
	campaign_image_url
	organization_id
	call_to_action
	sub_headline
`

const UPDATE_CAMPAIGN_TEMPLATE = gql`
	mutation updateCampaignTemplate($input: UpdateCampaignTemplateInput!){
		updateCampaignTemplate(input: $input){
			campaign_template{
				${return_fields}
			}
		}
	}
`

const CREATE_CAMPAIGN_TEMPLATE = gql`
	mutation createCampaignTemplate($input: CreateCampaignTemplateInput!){
		createCampaignTemplate(input: $input){
			campaign_template {
				${return_fields}
			}
		}
	}
`

const GET_CAMPAIGN_TEMPLATE = gql`
	query campaign_template($id: ID){
		campaign_template(id: $id){
			${return_fields}
		}
	}
`

function Form({ organization_id, current_template, handleClose }) {
	const [show_landing_page_backgrounds, setShowLandingPageBackgrounds] = useState(false)
	const campaign_topic_options = useCampaignTopicOptions()

	const { id: current_template_id, copy_template } = current_template

	const [template, setTemplate] = useState({
		highlights: [''],
		questions: [''],
	})

	const [fetchTemplate, { loading }] = useLazyQuery(GET_CAMPAIGN_TEMPLATE, {
		onCompleted: (result) =>
			setTemplate({
				...template,
				...result.campaign_template,
				name: `${result.campaign_template.name} ${current_template.copy_template ? '(Copy)' : ''}`,
			}),
	})

	useEffect(() => {
		if (current_template_id) {
			fetchTemplate({ variables: { id: current_template_id } })
		}
	}, [current_template_id, fetchTemplate])

	const [mutate] = useMutation(
		current_template_id && !copy_template ? UPDATE_CAMPAIGN_TEMPLATE : CREATE_CAMPAIGN_TEMPLATE,
		{
			onCompleted: handleClose,
		},
	)

	const [mutateCampaignImage, image_mutation_status] = useMutation(UPDATE_CAMPAIGN_TEMPLATE, {
		onCompleted: ({ updateCampaignTemplate }) => {
			setTemplate({
				...template,
				campaign_image_url: updateCampaignTemplate.campaign_template.campaign_image_url,
			})
		},
	})

	const handleInputChange = (e) => {
		const temp_template = { ...template }
		temp_template[e.target.name] = e.target.value

		if (e.target.name === 'is_meal_event') {
			temp_template.is_meal_event = Boolean(e.target.value)
		}

		setTemplate(temp_template)
	}

	const handleSubmit = (e) => {
		e.preventDefault()

		const variables = { ...template, organization_id }

		if (copy_template) {
			delete variables.id
		}

		delete variables.__typename

		mutate({ variables: { input: variables } })

		return false
	}

	if (!template) return null

	return (
		<React.Fragment>
			<Modal show modalDidClose={handleClose} style={{ width: 1000 }}>
				<div className="container" style={{ opacity: loading ? '0.5' : 1 }}>
					<div className="row">
						<div className="col-12">
							<h1>
								{current_template_id
									? `${copy_template ? 'Copy' : 'Edit'} Campaign Template`
									: 'Create a Campaign Template'}
							</h1>

							<form onSubmit={(e) => handleSubmit(e)} autoComplete="off">
								<div className="row">
									<div className="col-lg-6">
										<FloatingLabel
											label="Template Name"
											type="text"
											name="name"
											value={template.name || ''}
											onChange={handleInputChange}
											required
											prevent_autofill
										/>
									</div>
									<div className="col-lg-6">
										<FloatingLabel
											label="Text above headline"
											type="text"
											name="call_to_action"
											value={template.call_to_action || ''}
											onChange={handleInputChange}
											prevent_autofill
										/>
									</div>
									<div className="col-lg-6">
										<FloatingLabel
											label="Objective"
											type="select"
											name="objective"
											value={template.objective}
											options={Campaign.OBJECTIVE_OPTIONS}
											onChange={handleInputChange}
											required
											prevent_autofill
										/>
									</div>
									<div className="col-lg-6">
										<FloatingLabel
											label="Headline"
											type="text"
											name="headline"
											value={template.headline || ''}
											onChange={handleInputChange}
											required
											prevent_autofill
										/>
									</div>
									<div className="col-sm-6">
										<FloatingLabel
											label="Topic"
											type="select"
											name="topic"
											value={template.topic || ''}
											options={campaign_topic_options}
											onChange={handleInputChange}
											autoComplete="off"
											prevent_autofill
											required
										/>
									</div>
									<div className="col-lg-6">
										<FloatingLabel
											label="Text below headline"
											type="text"
											name="sub_headline"
											value={template.sub_headline || ''}
											onChange={handleInputChange}
											prevent_autofill
										/>
									</div>
									<div className="col-sm-6">
										<FloatingLabel
											label="Video Link"
											type="url"
											name="video_embed_link"
											value={template.video_embed_link || ''}
											onChange={handleInputChange}
											prevent_autofill
										/>
									</div>
									<div className="col-lg-3 col-sm-6">
										<div className="position-relative" style={{ height: '67px' }}>
											<label>
												<input
													name="is_meal_event"
													type="radio"
													value
													onChange={handleInputChange}
													checked={template.is_meal_event === true}
													style={{
														position: 'absolute',
														top: '40px',
														left: '10px',
														zIndex: '2',
													}}
													required
												/>
												<div
													className="form-text text-muted"
													style={{
														position: 'absolute',
														top: 32,
														paddingLeft: '35px',
														width: '100%',
													}}
												>
													Meal Events
												</div>
											</label>
										</div>
									</div>
									<div className="col-lg-3 col-sm-6">
										<div className="position-relative" style={{ height: '67px' }}>
											<label>
												<input
													name="is_meal_event"
													type="radio"
													value=""
													onChange={handleInputChange}
													checked={template.is_meal_event === false}
													style={{
														position: 'absolute',
														top: '40px',
														left: '10px',
														zIndex: '2',
													}}
													required
												/>
												<div
													className="form-text text-muted"
													style={{
														position: 'absolute',
														top: 32,
														paddingLeft: '35px',
														width: '100%',
													}}
												>
													Non-Meal Events
												</div>
											</label>
										</div>
									</div>
								</div>
								<div className="form-group">
									<div className="form-text text-muted mt-3 mb-2">Description</div>
									<MarkdownEditor
										value={template.description}
										setValue={(description) => setTemplate({ ...template, description })}
									/>
								</div>
								<div className="form-group">
									<div className="form-text text-muted">Highlights</div>
									<DragAndDropListSingleInput
										items={template.highlights || []}
										setItems={(highlights) =>
											handleInputChange({
												target: {
													name: 'highlights',
													value: highlights,
												},
											})
										}
										placeholder="Start typing..."
									/>
								</div>
								<Description2
									campaign_state={template}
									setCampaignState={(props) => setTemplate({ ...template, ...props })}
								/>

								<button
									type="button"
									className="btn btn-primary rounded mr-3"
									onClick={() => setShowLandingPageBackgrounds(true)}
								>
									Select Landing Page Background
								</button>

								{template.objective === 3 && (
									<div className="form-group">
										<div className="form-text text-muted">
											Campaign Image <MutationStatusIndicator {...image_mutation_status} />
										</div>
										{template.id && (
											<ImageUploader
												image_url={template.campaign_image_url}
												uploadImage={(image_file) =>
													mutateCampaignImage({
														variables: {
															image_file,
															id: template.id,
														},
													})
												}
												removeImage={() =>
													setTemplate({
														...template,
														campaign_image_url: '',
													})
												}
											/>
										)}
										{!template.id && (
											<em>Campaign image can be added after campaign template is created.</em>
										)}
									</div>
								)}
								<div className="text-center my-4">
									<button type="submit" className="btn btn-success rounded">
										Save Campaign Template
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			</Modal>

			<Modal
				show={show_landing_page_backgrounds}
				modalDidClose={() => setShowLandingPageBackgrounds(false)}
				style={{ width: '70%' }}
			>
				<LandingPageBackgroundSelector
					handleInputChange={handleInputChange}
					current_background_id={template.landing_page_background_id || ''}
				/>
			</Modal>
		</React.Fragment>
	)
}

Form.propTypes = {
	organization_id: PropTypes.string,
	current_template: PropTypes.object,
	handleClose: PropTypes.func,
}

export default Form
